import React, { useEffect, useState, useCallback } from 'react';
import {
  Container, Typography, Paper, Stack, TextField, MenuItem, Button, Box,
  Chip, IconButton, Divider, Alert, InputAdornment, Tooltip
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutline';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import { useTranslation } from 'react-i18next';
import { api } from '../../api';
import { formatMoney } from '../../utils/money';
import dayjs from 'dayjs';

const emptyForm = { code: '', discountType: 'percent', discountValue: '', maxUses: '', expiresAt: '' };

export default function Promos() {
  const { t } = useTranslation();
  const [promos, setPromos] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      setPromos(await api.getPromos());
    } catch (e) {
      setPromos([]);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleCreate = async () => {
    setError('');
    setSaving(true);
    try {
      await api.createPromo({
        code: form.code.trim(),
        discountType: form.discountType,
        discountValue: Number(form.discountValue),
        maxUses: form.maxUses === '' ? null : Number(form.maxUses),
        // діє до кінця обраного дня
        expiresAt: form.expiresAt ? dayjs(form.expiresAt).endOf('day').toISOString() : null,
      });
      setForm(emptyForm);
      await load();
    } catch (e) {
      setError(e.message || t('promos.createFailed'));
    }
    setSaving(false);
  };

  const toggleActive = async (p) => {
    try {
      await api.updatePromo(p.id, { active: !p.active });
      await load();
    } catch (e) { /* ignore */ }
  };

  const remove = async (p) => {
    if (!window.confirm(t('promos.confirmDelete', { code: p.code }))) return;
    try {
      await api.deletePromo(p.id);
      await load();
    } catch (e) { /* ignore */ }
  };

  const discountLabel = (p) =>
    p.discountType === 'percent' ? `−${p.discountValue}%` : `−${formatMoney(p.discountValue)}`;

  const statusChip = (p) => {
    const map = {
      active: { color: 'success', label: t('promos.statusActive') },
      disabled: { color: 'default', label: t('promos.statusDisabled') },
      expired: { color: 'warning', label: t('promos.statusExpired') },
      used_up: { color: 'error', label: t('promos.statusUsedUp') },
    };
    const s = map[p.status] || map.active;
    return <Chip size="small" color={s.color} label={s.label} />;
  };

  return (
    <Container maxWidth="md" sx={{ mt: 2 }}>
      <Typography variant="h5" sx={{ mb: 2 }}>{t('promos.title')}</Typography>

      {/* --- Форма створення --- */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>{t('promos.newTitle')}</Typography>
        <Stack spacing={2} direction={{ xs: 'column', sm: 'row' }} flexWrap="wrap" useFlexGap>
          <TextField
            label={t('promos.code')}
            value={form.code}
            onChange={(e) => setForm((f) => ({ ...f, code: e.target.value.toUpperCase() }))}
            placeholder="LATO20"
            sx={{ minWidth: 140 }}
          />
          <TextField select label={t('promos.type')} value={form.discountType} onChange={set('discountType')} sx={{ minWidth: 150 }}>
            <MenuItem value="percent">{t('promos.percent')}</MenuItem>
            <MenuItem value="amount">{t('promos.amount')}</MenuItem>
          </TextField>
          <TextField
            label={t('promos.value')}
            value={form.discountValue}
            onChange={set('discountValue')}
            inputProps={{ inputMode: 'decimal' }}
            sx={{ minWidth: 120 }}
            InputProps={{
              endAdornment: <InputAdornment position="end">{form.discountType === 'percent' ? '%' : 'zł'}</InputAdornment>,
            }}
          />
          <TextField
            label={t('promos.maxUses')}
            value={form.maxUses}
            onChange={set('maxUses')}
            inputProps={{ inputMode: 'numeric' }}
            placeholder={t('promos.unlimited')}
            sx={{ minWidth: 130 }}
          />
          <TextField
            label={t('promos.expiresAt')}
            type="date"
            value={form.expiresAt}
            onChange={set('expiresAt')}
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 160 }}
          />
        </Stack>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          {t('promos.hint')}
        </Typography>
        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
        <Button
          variant="contained"
          onClick={handleCreate}
          disabled={saving || !form.code.trim() || form.discountValue === ''}
          sx={{ mt: 2 }}
        >
          {saving ? t('common.saving') : t('promos.create')}
        </Button>
      </Paper>

      {/* --- Список --- */}
      <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>{t('promos.listTitle')}</Typography>
      {promos.length === 0 ? (
        <Typography variant="body2" color="text.secondary">{t('promos.empty')}</Typography>
      ) : (
        <Stack spacing={1.5}>
          {promos.map((p) => (
            <Paper key={p.id} sx={{ p: 2, opacity: p.status === 'active' ? 1 : 0.75 }}>
              <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" useFlexGap spacing={1}>
                <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
                  <Typography sx={{ fontWeight: 700, letterSpacing: '0.08em', fontFamily: 'monospace', fontSize: '1.05rem' }}>
                    {p.code}
                  </Typography>
                  <Chip size="small" color="primary" sx={{ fontWeight: 700 }} label={discountLabel(p)} />
                  {statusChip(p)}
                </Stack>
                <Stack direction="row" spacing={0.5}>
                  <Tooltip title={p.active ? t('promos.pause') : t('promos.resume')}>
                    <IconButton size="small" onClick={() => toggleActive(p)}>
                      {p.active ? <PauseCircleOutlineIcon /> : <PlayCircleOutlineIcon />}
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={t('common.delete')}>
                    <IconButton size="small" color="error" onClick={() => remove(p)}>
                      <DeleteOutlineIcon />
                    </IconButton>
                  </Tooltip>
                </Stack>
              </Stack>
              <Divider sx={{ my: 1 }} />
              <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
                <Typography variant="body2" color="text.secondary">
                  {t('promos.used')}: <b>{p.usedCount}{p.maxUses != null ? ` / ${p.maxUses}` : ' / ∞'}</b>
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('promos.expiresAt')}: <b>{p.expiresAt ? dayjs(p.expiresAt).format('DD.MM.YYYY') : t('promos.noExpiry')}</b>
                </Typography>
              </Stack>
            </Paper>
          ))}
        </Stack>
      )}
    </Container>
  );
}
